/* global AbortSignal */
/** Планы «Аналитики МП» (`/api/mp-plans`, backend `mp_plans/router.py` — источник правды форм
 *  ответов): месяц × площадка × юрлицо × показатель. Просмотр — право `ozon_analytics`, правки и
 *  копирование — `mp_plans_edit`. Темп, прогноз и отставание считает фронт (`planCalc.ts`,
 *  режимы прогноза — `planForecast.ts` по дневному ряду `daily`). */
import { api } from "./client";
import type { CogsCoverage } from "./mpAnalytics";

export type PlanMp = "ozon" | "wb";

/** Дневной ряд для прогноза (только у текущего месяца). `to` — дата полных данных владельца ряда
 *  (юрлицо — из `data_through` витрины, «без юрлица» и юрлицо только с планом — `data_to` площадки;
 *  площадка — min кабинетов с `active: true`); `fact` — факт с 1-го по `to`, `elapsed` — дней месяца по `to` (0 — `to` в прошлом месяце):
 *  прогноз считается от них. `values[код]` — до 28 полных дней по `to` (`from` — первый; короче —
 *  история юрлица/витрины короче) тем же расчётом, что факт (день без строк — 0; null — день без
 *  себестоимости у маржи). `from` null и пустые ряды — истории нет (юрлицо только с планом)
 *  или данные юрлица кончились раньше 28 дней до `data_to` площадки. */
export interface PlanDaily {
  from: string | null;
  to: string;
  elapsed: number;
  fact: MetricValues;
  values: Record<string, (number | null)[]>;
}

/** Показатель плана (`revenue` Выручка, `net_margin` Маржа;
 *  список расширяемый — порядок и подписи берём из ответа, а не из кода). */
export interface PlanMetric {
  code: string;
  label: string;
  /** факт от себестоимости: null, если месяц ею не покрыт («нет данных»), плашка покрытия */
  cogs: boolean;
}

/** Покрытие продаж себестоимостью за дни факта площадки (как `/cogs-coverage` раздела, все юрлица). */
export type PlanCoverage = Omit<CogsCoverage, "mart" | "from_cache">;

export interface PlanItem {
  mp: PlanMp;
  legal_name: string;
  metric: string;
  value: number;
  updated_at: string | null;
  /** логин последнего изменившего; null — пользователь удалён */
  updated_by: string | null;
}

export interface PlansList {
  year: number;
  month: number;
  metrics: PlanMetric[];
  items: PlanItem[];
}

export interface PlanPutBody {
  year: number;
  month: number;
  mp: PlanMp;
  legal_name: string;
  metric: string;
  /** null — удалить план ячейки */
  value: number | null;
}

export type PlanPutResult = PlanItem | { deleted: true };

export interface PlanLegal {
  /** null — факты без юрлица: план на них не задаётся */
  legal_name: string | null;
  /** диапазон данных юрлица в витрине; null — данных нет */
  date_from: string | null;
  date_to: string | null;
}

export type PlanLegalsMp = { mp: PlanMp; legals: PlanLegal[] } | { mp: PlanMp; error: string };

export interface PlanLegals {
  mps: PlanLegalsMp[];
}

/** Значения по показателям: код → число или null (плана нет / факта нет — будущий месяц; у
 *  показателя от себестоимости — месяц ею не покрыт). */
export type MetricValues = Record<string, number | null>;

export interface PlanFactRow {
  legal_name: string | null;
  /** дата полных данных юрлица (YYYY-MM-DD; `data_through` витрины, «без юрлица» — `data_to`
   *  площадки; она же — `daily.to`); null — юрлица нет в витрине (только план). Раньше
   *  `data_to` площадки — у юрлица фактов меньше дней (на экране — пометка). Необязательное, пока
   *  бэкенд не отдаёт его везде: нет поля — пометки нет. */
  data_to?: string | null;
  fact: MetricValues;
  plan: MetricValues;
  /** ряд юрлица по его дате полных данных; null — месяц не текущий */
  daily: PlanDaily | null;
}

export interface PlanFactMpOk {
  mp: PlanMp;
  label: string;
  /** конец данных витрины площадки (YYYY-MM-DD) */
  data_to: string;
  /** прошедшие дни месяца по данным площадки: 0 — будущий, = days_in_month — закрытый */
  days_elapsed: number;
  mart: Record<string, unknown>;
  /** покрытие себестоимостью за дни факта; null — дней факта нет (будущий месяц) */
  coverage: PlanCoverage | null;
  /** ошибки отдельных показателей {код: текст} (сейчас — `net_margin`: себестоимость не собрана);
   *  факт такого показателя — null, остальные показатели площадки посчитаны */
  metric_errors?: Record<string, string>;
  /** ряд площадки итогом (все юрлица) по её `data_to`; null — месяц не текущий */
  daily: PlanDaily | null;
  rows: PlanFactRow[];
}

export interface PlanFactMpError {
  mp: PlanMp;
  label: string;
  error: string;
}

export type PlanFactMp = PlanFactMpOk | PlanFactMpError;

export interface PlanFact {
  month: string;
  days_in_month: number;
  metrics: PlanMetric[];
  mps: PlanFactMp[];
}

export function isMpError(m: PlanFactMp): m is PlanFactMpError {
  return "error" in m;
}

const BASE = "/mp-plans";

export async function getPlans(year: number, month: number, signal?: AbortSignal): Promise<PlansList> {
  const { data } = await api.get<PlansList>(BASE, { params: { year, month }, signal });
  return data;
}

export async function putPlan(body: PlanPutBody): Promise<PlanPutResult> {
  const { data } = await api.put<PlanPutResult>(BASE, body);
  return data;
}

export async function copyPlans(year: number, month: number): Promise<{ copied: number }> {
  const { data } = await api.post<{ copied: number }>(`${BASE}/copy`, { year, month });
  return data;
}

export async function getPlanLegals(signal?: AbortSignal): Promise<PlanLegals> {
  const { data } = await api.get<PlanLegals>(`${BASE}/legals`, { signal });
  return data;
}

/** `month` — YYYY-MM. */
export async function getPlanFact(month: string, signal?: AbortSignal): Promise<PlanFact> {
  const { data } = await api.get<PlanFact>(`${BASE}/plan-fact`, { params: { month }, signal });
  return data;
}
